/**
 * Normalizes a route-derived page context with explicit project metadata.
 *
 * Why this exists:
 * - `getCurrentPageFromLocation` intentionally stays URL-only and pure.
 * - Some screens still need project context values (`projectShortname`, numeric
 *   `projectId`) even when the URL has no explicit project segment.
 * - The umbrella locale root (`/:locale`, e.g. `/de`) is a special case: this page
 *   must remain a site page (`site_startpage`) with no project identifiers.
 *
 * Rules:
 * 1. URL-derived `params.projectShortname` is authoritative when present.
 * 2. If missing, fallback to project context only outside the umbrella locale root case.
 * 3. Numeric `projectId` is only set when a project shortname is resolved.
 * 4. Umbrella locale root remaps `project_startpage` to `site_startpage`.
 */
export default function normalizeCurrentPageWithProjectContext(
    currentPage,
    projectContext = {}
) {
    const {
        isUmbrella = false,
        projectShortname = null,
        projectId = null,
    } = projectContext;

    // Check whether the route contains a project shortname param
    const routeProjectShortname = currentPage.params?.projectShortname ?? null;
    const isUmbrellaLocaleRoot =
        currentPage.pageType === 'project_startpage' &&
        !routeProjectShortname &&
        isUmbrella;

    const fallbackProjectShortname = projectShortname ?? null;
    const resolvedProjectShortname = isUmbrellaLocaleRoot
        ? null
        : (routeProjectShortname ?? fallbackProjectShortname);
    const resolvedProjectId = resolvedProjectShortname
        ? (projectId ?? null)
        : null;

    const normalizedCurrentPage = {
        ...currentPage,
        params: {
            ...currentPage.params,
            projectShortname: resolvedProjectShortname,
            projectId: resolvedProjectId,
        },
    };

    if (isUmbrellaLocaleRoot) {
        return {
            ...normalizedCurrentPage,
            pageType: 'site_startpage',
        };
    }

    return normalizedCurrentPage;
}
